import { newAttempt, type InteractiveAttempt } from '../core/TaskAttempt';
import { upgradeLevel } from '../data/coinUpgrades';
// Contextual panels. Each render builds static DOM once and returns an update()
// that patches text, so buttons and minigames survive the per-frame change events.
import { CURDLE, CUT, MILK, PASTEURIZE, RICOTTA, STRETCH, SUPPLIES, TALEGGIO, TASK, VAT, type SupplyId } from '../config/constants';
import { CHEESES, freshnessMultiplier } from '../data/cheeses';
import { PRICING_REVISION, PRICING_SOURCE_URL } from '../data/pricing';
import type { Channel } from '../core/Game';
import { exportSave, importSave, type InventoryCheese } from '../core/GameState';
import { marketDepth, retailBand } from '../core/Market';
import * as B from '../process/Batch';
import { holdButton, holdStretch, tempoStir, timingBar, type MinigameHandle } from './minigames';
import type { RenderResult } from './Panel';
import type { UI } from './UI';
import { cheeseArt } from '../art/cheeseArt';
import { objectArt } from '../art/worldArt';

function progressOptions(ui: UI, b: B.Batch | null, key: string, kind: InteractiveAttempt['kind']) {
  const attempts = b ? (b.interactiveAttempts ??= {}) : ui.game.state.interactiveAttempts;
  const attempt = attempts[key] ??= newAttempt(kind);
  return { attempt, onProgress: () => ui.game.save() };
}
function completeAttempt(ui: UI, b: B.Batch | null, key: string, action: () => void): void {
  const attempts = b ? b.interactiveAttempts : ui.game.state.interactiveAttempts;
  delete attempts?.[key];
  action(); ui.game.save();
}

export const $ = <T extends HTMLElement = HTMLElement>(root: HTMLElement, sel: string): T => root.querySelector(sel) as T;
export const fmt = (n: number, d = 2): string => n.toFixed(d);

export function starText(n: number): string {
  return '★'.repeat(Math.round(n)) + '☆'.repeat(5 - Math.round(n));
}

export function tempClass(t: number, lo: number, hi: number, hot: number): string {
  if (t > hot) return 'temp hot';
  if (t >= lo && t <= hi) return 'temp inband';
  return 'temp';
}

// ------------------------------------------------------------------ Bessie

export function openBessiePanel(ui: UI): void {
  const game = ui.game;
  ui.panels.open({
    id: 'bessie',
    title: 'Bessie',
    kind: 'task',
    side: ui.side(),
    render(body, api): RenderResult {
      body.innerHTML = `
        <div class="bessie-portrait">${objectArt('bessie')}</div>
        <div class="stat"><span>Location</span><b class="cow-location"></b></div>
        <div class="row"><button class="btn move-cow"></button></div>
        <div class="muted cow-location-hint"></div>
        <div class="stat"><span>Fed today</span><b class="fed"></b></div>
        <div class="stat"><span>Milking skill</span><b class="lvl"></b></div>
        <div class="stat"><span>Next milking</span><b class="cd"></b></div>
        <div class="section">
          <h4>Feed (once a day, before milking)</h4>
          <div class="row">
            <button class="btn feed-basic"></button>
            <button class="btn feed-quality"></button>
          </div>
          <div class="muted">Basic feed starts milk at 2.5 quality, quality feed at 3.8 with richer cream.</div>
        </div>
        <div class="section">
          <h4>Milk</h4>
          <div class="muted">15 game minutes, ${TASK.milk.energy} energy. Yield ${MILK.YIELD_MIN} to ${MILK.YIELD_MAX} gallons plus skill.</div>
          <div class="row"><button class="btn primary milk">Milk Bessie</button></div>
          <div class="muted status"></div>
          <div class="mg-slot"></div>
        </div>`;
      const moveCow = $<HTMLButtonElement>(body, '.move-cow');
      moveCow.onclick = () => game.moveBessie(game.state.bessie.location === 'pasture' ? 'barn' : 'pasture');
      const fed = $(body, '.fed');
      const lvl = $(body, '.lvl');
      const cd = $(body, '.cd');
      const feedBasic = $<HTMLButtonElement>(body, '.feed-basic');
      const feedQuality = $<HTMLButtonElement>(body, '.feed-quality');
      const milkBtn = $<HTMLButtonElement>(body, '.milk');
      const status = $(body, '.status');
      const slot = $(body, '.mg-slot');
      let mg: MinigameHandle | null = null;

      feedBasic.onclick = () => game.feedBessie('basic');
      feedQuality.onclick = () => game.feedBessie('quality');
      const attemptKey = Object.keys(game.state.interactiveAttempts).find(k => k.startsWith(`milk:${game.state.day}:`)) ?? `milk:${game.state.day}:${game.state.bessie.milkingsToday}`;
      const startMilking = () => {
        const existing = game.state.interactiveAttempts[attemptKey];
        const away = game.state.bessie.location === 'pasture' ? game.state.room !== 'field' : game.state.room !== 'barn';
        const reason = away ? 'Visit Bessie in ' + (game.state.bessie.location === 'pasture' ? 'Sunny Pasture.' : 'the barn.') : existing ? null : game.canMilk();
        if (reason) {
          game.toast(reason, 'bad');
          return;
        }
        milkBtn.disabled = true; moveCow.disabled = true;
        mg?.destroy();
        const progress = progressOptions(ui, null, attemptKey, 'timing');
        if (!progress.attempt.milk) {
          progress.attempt.milk = { day: game.state.day, feedType: game.state.bessie.feedType ?? 'basic',
            pasture: game.state.bessie.location === 'pasture', careLevel: upgradeLevel(game.state,'bessie_care'), effortLevel: upgradeLevel(game.state,'efficient_effort') };
          // Reserve immediately, just like staff, so a second worker cannot milk her.
          game.state.bessie.lastMilkedMinute = game.now;
          game.state.bessie.milkingsToday++;
          game.save();
        }
        mg = timingBar(slot, {
          ...progress,
          pulls: MILK.PULLS,
          zoneWidth: 0.22 * game.toleranceMult('milk'),
          periodMs: 1500,
          buttonLabel: 'Pull',
          hint: 'Stop the marker inside the blue zone. Three pulls.',
          onDone: (r) => {
            completeAttempt(ui, null, attemptKey, () => game.finishMilking(r.score, progress.attempt.milk));
            queueMicrotask(() => { if (body.isConnected) api.rerender(); });
          },
        });
      };

      milkBtn.onclick = startMilking;
      if (game.state.interactiveAttempts[attemptKey]) startMilking();
      const update = (): void => {
        const s = game.state;
        const grazing = s.bessie.location === 'pasture';
        $(body, '.cow-location').textContent = grazing ? 'Sunny Pasture · +15% milk' : 'Barn · standard yield';
        moveCow.textContent = grazing ? 'Bring Bessie to barn' : 'Move Bessie to pasture';
        moveCow.disabled = !game.hasSkill('field') || !!mg || s.staff.employees.some(e => e.job?.kind === 'milk' && e.job.phase === 'work');
        $(body, '.cow-location-hint').textContent = game.hasSkill('field') ? 'Grazing gives 15% more milk. Feed, cooldown and daily limits stay the same.' : 'Unlock Sunny Pasture for 350 coins in Farm Milestones after the Cellar.';
        fed.textContent = s.bessie.fedToday ? `Yes (${s.bessie.feedType})` : 'No';
        fed.className = s.bessie.fedToday ? 'good' : 'bad';
        lvl.textContent = `Level ${game.taskLevel('milk')} (${s.xp.tasks.milk} xp)`;
        const rem = game.milkCooldownRemaining();
        cd.textContent = !s.bessie.fedToday ? 'Feed her first' : rem > 0 ? `in ${Math.ceil(rem)} min` : 'Ready';
        feedBasic.textContent = `Basic feed (${s.supplies.feed_basic})`;
        feedQuality.textContent = `Quality feed (${s.supplies.feed_quality})`;
        feedBasic.disabled = s.bessie.fedToday || s.supplies.feed_basic <= 0;
        feedQuality.disabled = s.bessie.fedToday || s.supplies.feed_quality <= 0;
        const away = grazing ? s.room !== 'field' : s.room !== 'barn';
        const reason = away ? `Visit Bessie in ${grazing ? 'Sunny Pasture' : 'the barn'} to milk her.` : game.canMilk();
        status.textContent = reason ?? `Ready. Energy ${Math.round(s.energy)}${game.lowEnergy ? ' (low: windows shrink to 60%)' : ''}.`;
        if (!mg) milkBtn.disabled = !!reason;
      };
      update();
      return { update, cleanup: () => mg?.destroy() };
    },
  });
}

// --------------------------------------------------------------------- Vat

export function openVatPanel(ui: UI): void {
  const game = ui.game;
  ui.panels.open({
    id: 'vat',
    title: 'Cheese vat',
    kind: 'task',
    side: ui.side(),
    render(body, api): RenderResult {
      const b = game.batch;
      if (!b) return renderVatIdle(ui, body);
      return renderStage(ui, body, b, 'vat', api.rerender);
    },
  });
}

function renderVatIdle(ui: UI, body: HTMLElement): RenderResult {
  const game = ui.game;
  body.innerHTML = `
    <div class="stat"><span>Vat durability</span><b class="dur"></b></div>
    <div class="section"><h4>Start a batch</h4><div class="muted recipes"></div><div class="stacks"></div></div>
    <div class="muted">Capacity ${MILK.VAT_CAPACITY_GAL} gallons. Learn more recipes in the Codex (C).</div>`;
  const dur = $(body, '.dur');
  const recipes = $(body, '.recipes');
  const stacks = $(body, '.stacks');
  let sig = '';
  const update = (): void => {
    const s = game.state;
    dur.textContent = `${s.vat.durability}/${VAT.DURABILITY_MAX}${game.vatWorn ? ' (worn: −0.5 quality)' : ''}`;
    dur.className = game.vatWorn ? 'bad' : '';
    const milk = game.milkStacks();
    const whey = game.wheyStack();
    const learned = game.availableRecipes().map((d) => d.id);
    const nextSig = milk.map((m) => `${m.index}:${m.item.qty}:${m.item.quality}`).join('|') + `|whey:${whey?.item.qty ?? 0}|${learned.join(',')}|${s.skills.length}`;
    if (nextSig === sig) return;
    sig = nextSig;
    recipes.textContent = `Recipes you know: ${learned.map((id) => CHEESES[id].name).join(', ')}.`;
    stacks.innerHTML = '';
    if (!milk.length && !(whey && whey.item.qty >= RICOTTA.MIN_GALLONS)) {
      stacks.innerHTML = '<div class="muted">No raw milk. Milk Bessie in the Barn.</div>';
    }
    for (const m of milk) {
      const row = document.createElement('div');
      row.className = 'item';
      row.innerHTML = `<div class="title">${m.item.qty} gal raw milk</div><div class="muted">Quality ${fmt(m.item.quality, 1)} · ${m.item.cream}% cream</div><div class="row"></div>`;
      const r = $(row, '.row');
      for (const process of ['mozzarella', 'taleggio'] as B.Process[]) {
        const def = CHEESES[B.PROCESS_CHEESE[process]];
        const block = game.recipeBlock(def.id);
        const btn = document.createElement('button');
        btn.className = `btn small ${process === 'mozzarella' ? 'primary' : ''}`;
        btn.textContent = `${def.name}`;
        btn.title = block ?? `Pour into the vat for ${def.name}`;
        btn.disabled = !!block || m.item.qty > MILK.VAT_CAPACITY_GAL;
        btn.onclick = () => game.startBatch(m.index, process);
        r.appendChild(btn);
      }
      if (m.item.qty > MILK.VAT_CAPACITY_GAL) r.insertAdjacentHTML('beforeend', '<span class="bad">Too much for the vat</span>');
      stacks.appendChild(row);
    }
    if (whey && whey.item.qty >= RICOTTA.MIN_GALLONS) {
      const row = document.createElement('div');
      row.className = 'item';
      const block = game.recipeBlock('ricotta');
      const gal = Math.min(whey.item.qty, MILK.VAT_CAPACITY_GAL);
      row.innerHTML = `<div class="title">${whey.item.qty} gal whey</div><div class="muted">Quality ${fmt(whey.item.quality, 1)} · about ${Math.floor(gal * RICOTTA.LB_PER_GAL)} tubs of ricotta</div><div class="row"></div>`;
      const btn = document.createElement('button');
      btn.className = 'btn small primary';
      btn.textContent = `Ricotta from ${gal} gal`;
      btn.title = block ?? 'Reheat the whey for ricotta';
      btn.disabled = !!block;
      btn.onclick = () => game.startRicotta(gal);
      $(row, '.row').appendChild(btn);
      if (block) $(row, '.row').insertAdjacentHTML('beforeend', `<span class="muted">${block}</span>`);
      stacks.appendChild(row);
    }
  };
  update();
  return { update };
}

function stageTrack(b: B.Batch): string {
  const order = B.PROCESS_STAGES[b.process];
  const i = order.indexOf(b.stage);
  return `<div class="stage-track">${order.map((s, j) => `<span class="${j < i ? 'done' : j === i ? 'now' : ''}" title="${B.STAGE_LABEL[s]}"></span>`).join('')}</div>`;
}

function batchHeader(): string {
  return `
    <div class="stat"><span>Batch owner</span><b class="owner"></b></div>
    <div class="stat"><span>Perfection</span><b class="perfect"></b></div>
    <div class="stat"><span>Quality</span><b class="q"></b></div>
    <div class="stat"><span>Projected yield</span><b class="y"></b></div>
    <div class="log"></div>`;
}

/** Shared stage renderer for the vat, the draining table and the brine tub. */
export function renderStage(ui: UI, body: HTMLElement, b: B.Batch, slot: 'vat' | 'table' | 'tub', rerender: () => void): RenderResult {
  const game = ui.game;
  const def = CHEESES[B.PROCESS_CHEESE[b.process]];
  const stageBody = document.createElement('div');
  body.innerHTML = `${stageTrack(b)}<h4>${def.name}: ${B.STAGE_LABEL[b.stage]}</h4>${batchHeader()}`;
  body.appendChild(stageBody);
  const q = $(body, '.q');
  const owner = $(body, '.owner');
  const perfect = $(body, '.perfect');
  const y = $(body, '.y');
  const log = $(body, '.log');
  let logCount = -1;
  const updateHeader = (): void => {
    q.textContent = `${fmt(b.quality, 2)} (${starText(B.stars(b))})`;
    owner.textContent = b.ownerName;
    perfect.textContent = b.perfectedTasks.length ? b.perfectedTasks.join(', ') : 'none yet';
    y.textContent = `${fmt(B.projectedPounds(b), 1)} lb → ${B.projectedUnits(b)} ${def.unitLabel}${B.projectedUnits(b) === 1 ? '' : 's'} · yield ×${fmt(b.yieldFactor, 2)}`;
    if (b.penalties.length !== logCount) {
      logCount = b.penalties.length;
      log.innerHTML = b.penalties
        .slice(-4)
        .map((p) => `<div class="bad">${B.formatPenalty(p)}</div>`)
        .join('');
    }
  };

  let inner: RenderResult = {};
  switch (b.stage) {
    case 'pasteurize':
      inner = stagePasteurize(ui, stageBody, b);
      break;
    case 'curdle':
      inner = stageCurdle(ui, stageBody, b, rerender);
      break;
    case 'culture':
      inner = stageCulture(ui, stageBody, b, rerender);
      break;
    case 'rennet':
      inner = stageRennet(ui, stageBody, b, rerender);
      break;
    case 'cut':
      inner = stageCut(ui, stageBody, b, rerender);
      break;
    case 'stretch':
      inner = stageStretch(ui, stageBody, b, rerender);
      break;
    case 'bag':
    case 'tub':
      inner = stageBag(ui, stageBody, b);
      break;
    case 'heat':
      inner = stageHeat(ui, stageBody, b);
      break;
    case 'acidify':
      inner = stageAcidify(ui, stageBody, b);
      break;
    case 'skim':
      inner = stageSkim(ui, stageBody, b);
      break;
    case 'drain':
      inner = stageDrain(ui, stageBody, b);
      break;
    case 'mold':
      inner = stageMold(ui, stageBody, b, rerender);
      break;
    case 'brine':
      inner = stageBrine(ui, stageBody, b);
      break;
    case 'rack':
      inner = stageRack(ui, stageBody, b);
      break;
  }
  const discard = document.createElement('div');
  discard.className = 'section row';
  discard.innerHTML = `<button class="btn small danger">Discard batch</button><span class="muted">Focus Mode: the clock runs ×4 while this panel is open.</span>`;
  $<HTMLButtonElement>(discard, 'button').onclick = () => {
    if (confirm('Throw away this batch?')) game.discardBatch(slot);
  };
  body.appendChild(discard);

  updateHeader();
  return {
    update() {
      updateHeader();
      inner.update?.();
    },
    cleanup: inner.cleanup,
  };
}

function burnerRow(): string {
  return `
    <div class="row">
      <span class="muted">Burner</span>
      <button class="btn small b-off">Off</button>
      <button class="btn small b-low">Low</button>
      <button class="btn small b-high">High</button>
    </div>`;
}

function bindBurner(el: HTMLElement, ui: UI, b: B.Batch): () => void {
  const btns = { off: $<HTMLButtonElement>(el, '.b-off'), low: $<HTMLButtonElement>(el, '.b-low'), high: $<HTMLButtonElement>(el, '.b-high') };
  (Object.keys(btns) as B.Burner[]).forEach((k) => (btns[k].onclick = () => ui.game.setBurner(k)));
  return () => (Object.keys(btns) as B.Burner[]).forEach((k) => btns[k].classList.toggle('active', b.burner === k));
}

function stagePasteurize(ui: UI, el: HTMLElement, b: B.Batch): RenderResult {
  const game = ui.game;
  el.innerHTML = `
    <div class="muted">Hold <b>${PASTEURIZE.TARGET}°F</b> (${PASTEURIZE.BAND_LOW} to ${PASTEURIZE.BAND_HIGH}) for ${PASTEURIZE.HOLD_MINUTES} minutes. Above ${PASTEURIZE.SCORCH_TEMP}°F scorches. Pull it as soon as it is ready.</div>
    <div class="row"><span class="temp"></span><span class="muted vel"></span></div>
    <div class="progress"><div class="held"></div></div>
    <div class="stat"><span>Held in range</span><b class="heldtxt"></b></div>
    ${burnerRow()}
    <div class="row"><button class="btn primary pull">Pull the milk</button><span class="muted ready"></span></div>`;
  const temp = $(el, '.temp');
  const vel = $(el, '.vel');
  const held = $(el, '.held');
  const heldtxt = $(el, '.heldtxt');
  const paintBurner = bindBurner(el, ui, b);
  const pull = $<HTMLButtonElement>(el, '.pull');
  const ready = $(el, '.ready');
  pull.onclick = () => game.pullPasteurized();
  const update = (): void => {
    temp.textContent = `${fmt(b.temp, 1)}°F`;
    temp.className = tempClass(b.temp, PASTEURIZE.BAND_LOW, PASTEURIZE.BAND_HIGH, PASTEURIZE.SCORCH_TEMP);
    vel.textContent = `${b.tempVel >= 0 ? '▲' : '▼'} ${fmt(Math.abs(b.tempVel), 1)}°/min`;
    const pct = Math.min(100, (b.heldMinutes / PASTEURIZE.HOLD_MINUTES) * 100);
    held.style.width = `${pct}%`;
    heldtxt.textContent = `${fmt(Math.min(b.heldMinutes, PASTEURIZE.HOLD_MINUTES), 1)} / ${PASTEURIZE.HOLD_MINUTES} min`;
    paintBurner();
    pull.disabled = !b.pasteurizeReady;
    ready.textContent = b.pasteurizeReady ? (b.temp >= PASTEURIZE.OVERHOLD_TEMP ? 'Ready! Pull now, it is degrading.' : 'Ready.') : '';
    ready.className = b.pasteurizeReady ? 'bad' : 'muted';
  };
  update();
  return { update };
}

function stageCurdle(ui: UI, el: HTMLElement, b: B.Batch, rerender: () => void): RenderResult {
  const game = ui.game;
  el.innerHTML = `
    <div class="muted">Cool the milk to <b>${CURDLE.BAND_LOW} to ${CURDLE.BAND_HIGH}°F</b> (stop a few degrees early, it keeps dropping), add citric acid within ${CURDLE.MAX_WAIT_MINUTES} minutes of reaching temperature, then stir on tempo.</div>
    <div class="row"><span class="temp"></span><span class="muted vel"></span></div>
    <div class="row">
      <button class="btn small cool">Cooling: off</button>
      <button class="btn primary acid">Add citric acid</button>
      <span class="muted acidinfo"></span>
    </div>
    <div class="mg-slot"></div>`;
  const temp = $(el, '.temp');
  const vel = $(el, '.vel');
  const cool = $<HTMLButtonElement>(el, '.cool');
  const acid = $<HTMLButtonElement>(el, '.acid');
  const acidinfo = $(el, '.acidinfo');
  const slot = $(el, '.mg-slot');
  let mg: MinigameHandle | null = null;
  cool.onclick = () => game.setCooling(!b.cooling);
  acid.onclick = () => {
    if (game.addCitricAcid()) rerender();
  };
  if (b.acidAdded && !b.curdleStirred) {
    mg = tempoStir(slot, {
      ...progressOptions(ui, b, 'curdle', 'tempo'),
      beats: CURDLE.BEATS,
      beatMs: CURDLE.BEAT_MS,
      toleranceMs: CURDLE.TOLERANCE_MS * game.toleranceMult('curdle'),
      buttonLabel: 'Stir',
      hint: 'Stir on the beat. Off-tempo stirs cost quality.',
      onDone: (r) => completeAttempt(ui, b, 'curdle', () => game.finishCurdleStir(r.hits, r.misses)),
    });
  }
  const update = (): void => {
    temp.textContent = `${fmt(b.temp, 1)}°F`;
    temp.className = tempClass(b.temp, CURDLE.BAND_LOW, CURDLE.BAND_HIGH, 999);
    vel.textContent = `${b.tempVel >= 0 ? '▲' : '▼'} ${fmt(Math.abs(b.tempVel), 1)}°/min`;
    cool.textContent = `Cooling: ${b.cooling ? 'on' : 'off'}`;
    cool.classList.toggle('active', b.cooling);
    cool.disabled = b.acidAdded;
    acid.disabled = b.acidAdded;
    acidinfo.textContent = b.acidAdded
      ? `Added at ${fmt(b.acidTemp ?? 0, 1)}°F`
      : `${game.state.supplies.citric_acid} in stock${b.temp < CURDLE.BAND_LOW || b.temp > CURDLE.BAND_HIGH ? ' · out of range!' : ' · in range'}`;
  };
  update();
  return { update, cleanup: () => mg?.destroy() };
}

function stageCulture(ui: UI, el: HTMLElement, b: B.Batch, rerender: () => void): RenderResult {
  const game = ui.game;
  el.innerHTML = `
    <div class="muted">Cool to <b>${TALEGGIO.CULTURE_BAND_LOW} to ${TALEGGIO.CULTURE_BAND_HIGH}°F</b>, add the starter culture, then let it ripen <b>${TALEGGIO.RIPEN_MIN} to ${TALEGGIO.RIPEN_MAX} minutes</b> before moving on.</div>
    <div class="row"><span class="temp"></span><span class="muted vel"></span></div>
    <div class="row">
      <button class="btn small cool">Cooling: off</button>
      <button class="btn primary culture">Add culture</button>
      <span class="muted cinfo"></span>
    </div>
    <div class="ripen" style="display:none">
      <div class="row"><span class="temp rtime"></span><span class="muted">minutes ripening</span></div>
      <div class="progress"><div class="rbar"></div></div>
      <div class="row"><button class="btn primary check">Move on to rennet</button><span class="muted rwin"></span></div>
    </div>`;
  const temp = $(el, '.temp');
  const vel = $(el, '.vel');
  const cool = $<HTMLButtonElement>(el, '.cool');
  const cult = $<HTMLButtonElement>(el, '.culture');
  const cinfo = $(el, '.cinfo');
  const ripen = $(el, '.ripen');
  const rtime = $(el, '.rtime');
  const rbar = $(el, '.rbar');
  const check = $<HTMLButtonElement>(el, '.check');
  const rwin = $(el, '.rwin');
  cool.onclick = () => game.setCooling(!b.cooling);
  cult.onclick = () => {
    if (game.addCulture()) rerender();
  };
  check.onclick = () => game.checkRipening();
  const update = (): void => {
    temp.textContent = `${fmt(b.temp, 1)}°F`;
    temp.className = tempClass(b.temp, TALEGGIO.CULTURE_BAND_LOW, TALEGGIO.CULTURE_BAND_HIGH, 999);
    vel.textContent = `${b.tempVel >= 0 ? '▲' : '▼'} ${fmt(Math.abs(b.tempVel), 1)}°/min`;
    cool.textContent = `Cooling: ${b.cooling ? 'on' : 'off'}`;
    cool.classList.toggle('active', b.cooling);
    cool.disabled = b.cultureAdded;
    cult.disabled = b.cultureAdded;
    cinfo.textContent = b.cultureAdded ? `Added at ${fmt(b.cultureTemp ?? 0, 1)}°F` : `${game.state.supplies.culture} in stock${B.cultureInBand(b) ? ' · in range' : ' · out of range!'}`;
    ripen.style.display = b.cultureAdded ? '' : 'none';
    if (b.cultureAdded) {
      const t = B.ripenMinutes(b, game.now);
      rtime.textContent = fmt(t, 1);
      rtime.className = `temp ${t >= TALEGGIO.RIPEN_MIN && t <= TALEGGIO.RIPEN_MAX ? 'inband' : t > TALEGGIO.RIPEN_MAX ? 'hot' : ''}`;
      rbar.style.width = `${Math.min(100, (t / (TALEGGIO.RIPEN_MAX + 5)) * 100)}%`;
      rwin.textContent = t < TALEGGIO.RIPEN_MIN ? `Wait ${fmt(TALEGGIO.RIPEN_MIN - t, 1)} more min` : t <= TALEGGIO.RIPEN_MAX ? 'In the window. Move on!' : 'Late! Move on now.';
      rwin.className = t < TALEGGIO.RIPEN_MIN ? 'muted' : t <= TALEGGIO.RIPEN_MAX ? 'good' : 'bad';
    }
  };
  update();
  return { update };
}

function stageRennet(ui: UI, el: HTMLElement, b: B.Batch, rerender: () => void): RenderResult {
  const game = ui.game;
  const w = B.setWindow(b);
  const taleggio = b.process === 'taleggio';
  el.innerHTML = `
    <div class="muted">Add rennet, stir for 30 seconds, then let the curd set. Check it between <b>${w.min} and ${w.max} minutes</b>. Early costs yield, late costs quality.${taleggio ? ' The set curd goes to the draining table.' : ''}</div>
    <div class="row"><button class="btn primary add">Add rennet</button><span class="muted stock"></span></div>
    <div class="mg-slot"></div>
    <div class="set" style="display:none">
      <div class="row"><span class="temp settime"></span><span class="muted">minutes since rennet</span></div>
      <div class="progress"><div class="setbar"></div></div>
      <div class="row"><button class="btn primary check">${taleggio ? 'Carry to the draining table' : 'Check the curd'}</button><span class="muted setwindow"></span></div>
    </div>`;
  const add = $<HTMLButtonElement>(el, '.add');
  const stock = $(el, '.stock');
  const slot = $(el, '.mg-slot');
  const set = $(el, '.set');
  const settime = $(el, '.settime');
  const setbar = $(el, '.setbar');
  const check = $<HTMLButtonElement>(el, '.check');
  const win = $(el, '.setwindow');
  let mg: MinigameHandle | null = null;
  add.onclick = () => {
    if (game.addRennet()) rerender();
  };
  check.onclick = () => {
    if (taleggio) {
      if (game.carryToTable()) ui.closePanel();
    } else game.checkSet();
  };
  if (b.rennetAdded && !b.rennetStirred) {
    mg = holdButton(slot, {
      ...progressOptions(ui, b, 'rennet', 'hold'),
      ms: 2000,
      buttonLabel: 'Stir 30 s',
      hint: 'Hold to stir the rennet in gently.',
      onDone: () => {
        completeAttempt(ui, b, 'rennet', () => game.finishRennetStir());
        rerender();
      },
    });
  }
  const update = (): void => {
    add.disabled = b.rennetAdded;
    stock.textContent = b.rennetAdded ? 'Added' : `${game.state.supplies.rennet} in stock`;
    set.style.display = b.rennetStirred ? '' : 'none';
    if (b.rennetStirred) {
      const t = B.setMinutes(b, game.now);
      settime.textContent = fmt(t, 1);
      settime.className = `temp ${t >= w.min && t <= w.max ? 'inband' : t > w.max ? 'hot' : ''}`;
      setbar.style.width = `${Math.min(100, (t / (w.max + 2)) * 100)}%`;
      win.textContent = t < w.min ? `Wait ${fmt(w.min - t, 1)} more min` : t <= w.max ? 'In the window. Go!' : 'Late! Go now.';
      win.className = t < w.min ? 'muted' : t <= w.max ? 'good' : 'bad';
    }
  };
  update();
  return { update, cleanup: () => mg?.destroy() };
}

function stageCut(ui: UI, el: HTMLElement, b: B.Batch, rerender: () => void): RenderResult {
  const game = ui.game;
  el.innerHTML = `
    <div class="muted">Slice the curd in ${CUT.SLICES} passes, drain the whey, then stir <b>gently</b> until the curds firm up. Stirring too fast breaks them.</div>
    <div class="row"><button class="btn primary slice">Slice</button><span class="muted slices"></span></div>
    <div class="row"><button class="btn primary drain">Drain whey</button><span class="muted drained"></span></div>
    <div class="mg-slot"></div>`;
  const slice = $<HTMLButtonElement>(el, '.slice');
  const slices = $(el, '.slices');
  const drain = $<HTMLButtonElement>(el, '.drain');
  const drained = $(el, '.drained');
  const slot = $(el, '.mg-slot');
  let mg: MinigameHandle | null = null;
  slice.onclick = () => game.slice('vat');
  drain.onclick = () => {
    game.drainWhey();
    rerender();
  };
  if (b.drained && !b.firmed) {
    mg = tempoStir(slot, {
      ...progressOptions(ui, b, 'firm', 'tempo'),
      beats: CUT.BEATS,
      beatMs: CUT.BEAT_MS,
      toleranceMs: CUT.TOLERANCE_MS * game.toleranceMult('cut'),
      buttonLabel: 'Stir gently',
      hint: 'Slow beat. Only stirring too fast hurts here.',
      onDone: (r) => completeAttempt(ui, b, 'firm', () => game.finishFirming(r.early)),
    });
  }
  const update = (): void => {
    slice.disabled = b.slices >= CUT.SLICES;
    slices.textContent = `${b.slices} / ${CUT.SLICES} passes`;
    drain.disabled = b.slices < CUT.SLICES || b.drained;
    drained.textContent = b.drained ? 'Whey drained' : b.slices < CUT.SLICES ? 'Slice first' : 'Ready to drain';
  };
  update();
  return { update, cleanup: () => mg?.destroy() };
}

function stageStretch(ui: UI, el: HTMLElement, b: B.Batch, rerender: () => void): RenderResult {
  const game = ui.game;
  el.innerHTML = `
    <div class="muted">Heat the curd to <b>${STRETCH.TARGET_TEMP}°F</b> (over ${STRETCH.OVERHEAT_TEMP} hurts), then stretch ${STRETCH.PULLS} times and form the balls.</div>
    <div class="row"><span class="temp"></span><button class="btn small heat">Heat: off</button></div>
    <div class="mg-slot"></div>`;
  const temp = $(el, '.temp');
  const heat = $<HTMLButtonElement>(el, '.heat');
  const slot = $(el, '.mg-slot');
  let mg: MinigameHandle | null = null;
  let mounted = false;
  heat.onclick = () => game.setHeating(!b.heating);
  const mount = (): void => {
    mounted = true;
    const c = (STRETCH.ZONE_START + STRETCH.ZONE_END) / 2;
    const half = ((STRETCH.ZONE_END - STRETCH.ZONE_START) / 2) * game.toleranceMult('stretch');
    mg = holdStretch(slot, {
      ...progressOptions(ui, b, 'stretch', 'stretch'),
      pulls: STRETCH.PULLS,
      zoneStart: c - half,
      zoneEnd: c + half,
      fillMs: STRETCH.FILL_MS,
      buttonLabel: 'Stretch',
      hint: 'Hold to stretch, release in the zone. Holding to the end tears it.',
      onDone: (r) => completeAttempt(ui, b, 'stretch', () => game.finishStretching(r.hits, r.misses)),
    });
    void rerender;
  };
  const update = (): void => {
    temp.textContent = `${fmt(b.temp, 1)}°F`;
    temp.className = tempClass(b.temp, STRETCH.TARGET_TEMP - 5, STRETCH.OVERHEAT_TEMP, STRETCH.OVERHEAT_TEMP);
    heat.textContent = `Heat: ${b.heating ? 'on' : 'off'}`;
    heat.classList.toggle('active', b.heating);
    heat.disabled = b.stretched;
    if (!mounted && B.canStretch(b)) mount();
  };
  update();
  return { update, cleanup: () => mg?.destroy() };
}

function stageBag(ui: UI, el: HTMLElement, b: B.Batch): RenderResult {
  const game = ui.game;
  const def = CHEESES[B.PROCESS_CHEESE[b.process]];
  el.innerHTML = `
    <div class="stat"><span>${def.unitLabel}s ready</span><b class="balls"></b></div>
    <div class="stat"><span>Packaging in stock</span><b class="pack"></b></div>
    <div class="row"><button class="btn primary bag">Pack them</button><button class="btn eat">Eat one</button></div>
    <div class="muted">Unpacked cheese spoils overnight. Packaging is sold at the supply desk.</div>`;
  const balls = $(el, '.balls');
  const preview = document.createElement('p');
  preview.className = 'muted price-preview';
  el.appendChild(preview);
  const pack = $(el, '.pack');
  const bag = $<HTMLButtonElement>(el, '.bag');
  const eat = $<HTMLButtonElement>(el, '.eat');
  bag.onclick = () => game.bagBatch();
  eat.onclick = () => game.eatUnbagged();
  const update = (): void => {
    balls.textContent = `${b.units} × ${starText(B.stars(b))}`;
    preview.textContent = batchPricePreview(ui, b);
    pack.textContent = String(game.state.supplies.packaging);
    pack.className = game.state.supplies.packaging < b.units ? 'bad' : '';
    bag.disabled = b.units === 0 || game.state.supplies.packaging === 0;
    eat.disabled = b.units === 0;
  };
  update();
  return { update };
}

// ---- ricotta stages

function batchPricePreview(ui: UI, batch: B.Batch): string {
  const game = ui.game;
  const item: InventoryCheese = { kind: 'cheese', cheeseId: B.PROCESS_CHEESE[batch.process],
    qty: batch.units, stars: B.stars(batch), madeDay: game.state.day, agedMult: 1 };
  const price = game.cheesePrice(item, 'wholesale');
  return `${item.stars}★ · ${fmt(price.base, 4)} coins/lb base · ${fmt(item.qty * price.pounds)} lb total · aging ×${fmt(price.aging)}. Current wholesale quote after demand: ${fmt(game.quoteSale(item, item.qty, 'wholesale'))} coins. Packaging does not sell the batch.`;
}

function stageHeat(ui: UI, el: HTMLElement, b: B.Batch): RenderResult {
  const game = ui.game;
  el.innerHTML = `
    <div class="muted">Heat the whey to <b>${RICOTTA.TARGET}°F</b> (${RICOTTA.BAND_LOW} to ${RICOTTA.BAND_HIGH}). Above ${RICOTTA.SCORCH_TEMP}°F scorches. Add the acid while it is in the band.</div>
    <div class="row"><span class="temp"></span><span class="muted vel"></span></div>
    ${burnerRow()}
    <div class="row"><button class="btn primary acid">Add citric acid</button><span class="muted acidinfo"></span></div>`;
  const temp = $(el, '.temp');
  const vel = $(el, '.vel');
  const paintBurner = bindBurner(el, ui, b);
  const acid = $<HTMLButtonElement>(el, '.acid');
  const acidinfo = $(el, '.acidinfo');
  acid.onclick = () => game.addRicottaAcid();
  const update = (): void => {
    temp.textContent = `${fmt(b.temp, 1)}°F`;
    temp.className = tempClass(b.temp, RICOTTA.BAND_LOW, RICOTTA.BAND_HIGH, RICOTTA.SCORCH_TEMP);
    vel.textContent = `${b.tempVel >= 0 ? '▲' : '▼'} ${fmt(Math.abs(b.tempVel), 1)}°/min`;
    paintBurner();
    acidinfo.textContent = `${game.state.supplies.citric_acid} in stock${B.ricottaInBand(b) ? ' · in range' : ' · out of range!'}`;
    acidinfo.className = B.ricottaInBand(b) ? 'good' : 'muted';
  };
  update();
  return { update };
}

function stageAcidify(ui: UI, el: HTMLElement, b: B.Batch): RenderResult {
  const game = ui.game;
  el.innerHTML = `
    <div class="muted">Let it rest <b>without stirring</b> for ${RICOTTA.REST_MIN} to ${RICOTTA.REST_MAX} minutes while the curd rises. Skimming early loses curd; leaving it too long toughens it.</div>
    <div class="row"><span class="temp rtime"></span><span class="muted">minutes resting</span></div>
    <div class="progress"><div class="rbar"></div></div>
    <div class="row"><button class="btn primary skim">Start skimming</button><span class="muted rwin"></span></div>`;
  const rtime = $(el, '.rtime');
  const rbar = $(el, '.rbar');
  const skim = $<HTMLButtonElement>(el, '.skim');
  const rwin = $(el, '.rwin');
  skim.onclick = () => game.startSkim();
  const update = (): void => {
    const t = B.restMinutes(b, game.now);
    rtime.textContent = fmt(t, 1);
    rtime.className = `temp ${t >= RICOTTA.REST_MIN && t <= RICOTTA.REST_MAX ? 'inband' : t > RICOTTA.REST_MAX ? 'hot' : ''}`;
    rbar.style.width = `${Math.min(100, (t / (RICOTTA.REST_MAX + 5)) * 100)}%`;
    rwin.textContent = t < RICOTTA.REST_MIN ? `Wait ${fmt(RICOTTA.REST_MIN - t, 1)} more min` : t <= RICOTTA.REST_MAX ? 'The curd has risen. Skim!' : 'Late! Skim now.';
    rwin.className = t < RICOTTA.REST_MIN ? 'muted' : t <= RICOTTA.REST_MAX ? 'good' : 'bad';
  };
  update();
  return { update };
}

function stageSkim(ui: UI, el: HTMLElement, b: B.Batch): RenderResult {
  const game = ui.game;
  el.innerHTML = `<div class="muted">Skim ${RICOTTA.SKIM_SCOOPS} scoops of curd into the basket. Stop the ladle in the zone; a rough scoop breaks curd back into the whey.</div><div class="mg-slot"></div>`;
  const mg = timingBar($(el, '.mg-slot'), {
    ...progressOptions(ui, b, 'skim', 'timing'),
    pulls: RICOTTA.SKIM_SCOOPS,
    zoneWidth: RICOTTA.SKIM_ZONE * game.toleranceMult('cut'),
    periodMs: RICOTTA.SKIM_PERIOD_MS,
    buttonLabel: 'Scoop',
    hint: 'Gently now.',
    onDone: (r) => completeAttempt(ui, b, 'skim', () => game.finishSkim(r.hits, r.misses)),
  });
  void b;
  return { cleanup: () => mg.destroy() };
}

function stageDrain(ui: UI, el: HTMLElement, b: B.Batch): RenderResult {
  const game = ui.game;
  el.innerHTML = `
    <div class="muted">The basket drains. Give it at least <b>${RICOTTA.DRAIN_MIN} minutes</b>. You can walk away; it keeps draining on the clock.</div>
    <div class="row"><span class="temp dtime"></span><span class="muted">minutes draining</span></div>
    <div class="progress"><div class="dbar"></div></div>
    <div class="row"><button class="btn primary tub">Tub the ricotta</button><span class="muted dwin"></span></div>`;
  const dtime = $(el, '.dtime');
  const dbar = $(el, '.dbar');
  const tub = $<HTMLButtonElement>(el, '.tub');
  const dwin = $(el, '.dwin');
  tub.onclick = () => game.finishRicottaDrain();
  const update = (): void => {
    const t = B.drainMinutes(b, game.now);
    dtime.textContent = fmt(t, 1);
    dtime.className = `temp ${t >= RICOTTA.DRAIN_MIN ? 'inband' : ''}`;
    dbar.style.width = `${Math.min(100, (t / RICOTTA.DRAIN_MIN) * 100)}%`;
    tub.disabled = !B.canTub(b, game.now);
    dwin.textContent = t >= RICOTTA.DRAIN_MIN ? 'Drained enough.' : `${fmt(RICOTTA.DRAIN_MIN - t, 1)} min to go`;
    dwin.className = t >= RICOTTA.DRAIN_MIN ? 'good' : 'muted';
  };
  update();
  return { update };
}

// ---- taleggio stages at the table and tub

function stageMold(ui: UI, el: HTMLElement, b: B.Batch, rerender: () => void): RenderResult {
  const game = ui.game;
  el.innerHTML = `
    <div class="muted">Cut the curd in ${TALEGGIO.SLICES} passes to walnut size, ladle it into the square molds, then drain <b>${TALEGGIO.DRAIN_MINUTES} minutes</b>, turning the molds every ${TALEGGIO.TURN_EVERY}. Missed turns cost quality.</div>
    <div class="row"><button class="btn primary slice">Cut</button><span class="muted slices"></span></div>
    <div class="row"><button class="btn primary fill">Ladle into molds</button><span class="muted fillinfo"></span></div>
    <div class="mg-slot"></div>
    <div class="draining" style="display:none">
      <div class="row"><span class="temp mtime"></span><span class="muted">minutes draining</span></div>
      <div class="progress"><div class="mbar"></div></div>
      <div class="row"><button class="btn primary turn">Turn the molds</button><span class="muted turns"></span></div>
      <div class="row"><button class="btn primary carry">Carry to the brine tub</button></div>
    </div>`;
  const slice = $<HTMLButtonElement>(el, '.slice');
  const slices = $(el, '.slices');
  const fill = $<HTMLButtonElement>(el, '.fill');
  const fillinfo = $(el, '.fillinfo');
  const slot = $(el, '.mg-slot');
  const draining = $(el, '.draining');
  const mtime = $(el, '.mtime');
  const mbar = $(el, '.mbar');
  const turn = $<HTMLButtonElement>(el, '.turn');
  const turns = $(el, '.turns');
  const carry = $<HTMLButtonElement>(el, '.carry');
  let mg: MinigameHandle | null = null;
  slice.onclick = () => game.slice('table');
  fill.onclick = () => {
    fill.disabled = true;
    mg?.destroy();
    mg = holdButton(slot, {
      ...progressOptions(ui, b, 'ladle', 'hold'),
      ms: 2000,
      buttonLabel: 'Ladle',
      hint: 'Hold to ladle the curd gently into the molds.',
      onDone: () => {
        completeAttempt(ui, b, 'ladle', () => game.fillMolds());
        rerender();
      },
    });
  };
  turn.onclick = () => game.turnMolds();
  carry.onclick = () => {
    if (game.carryToTub()) ui.closePanel();
  };
  const update = (): void => {
    slice.disabled = b.slices >= TALEGGIO.SLICES || b.moldStartMinute !== null;
    slices.textContent = `${b.slices} / ${TALEGGIO.SLICES} passes`;
    if (!mg) fill.disabled = b.slices < TALEGGIO.SLICES || b.moldStartMinute !== null;
    fillinfo.textContent = b.moldStartMinute !== null ? 'Molds filled' : b.slices < TALEGGIO.SLICES ? 'Cut first' : 'Ready to ladle';
    draining.style.display = b.moldStartMinute !== null ? '' : 'none';
    if (b.moldStartMinute !== null) {
      const t = B.moldMinutes(b, game.now);
      mtime.textContent = fmt(Math.min(t, TALEGGIO.DRAIN_MINUTES), 0);
      mtime.className = `temp ${b.moldDone ? 'inband' : ''}`;
      mbar.style.width = `${Math.min(100, (t / TALEGGIO.DRAIN_MINUTES) * 100)}%`;
      const window = Math.min(Math.floor(t / TALEGGIO.TURN_EVERY), TALEGGIO.DRAIN_MINUTES / TALEGGIO.TURN_EVERY - 1);
      const turnedNow = b.moldTurns.some((m) => Math.floor(m / TALEGGIO.TURN_EVERY) === window);
      turn.disabled = b.moldDone || turnedNow;
      turns.textContent = b.moldDone ? `Drained. ${b.moldTurns.length} turns.` : turnedNow ? `Turned for this window (${b.moldTurns.length} so far)` : `Turn now (window ${window + 1} of ${TALEGGIO.DRAIN_MINUTES / TALEGGIO.TURN_EVERY})`;
      turns.className = b.moldDone ? 'good' : turnedNow ? 'muted' : 'accent';
      carry.disabled = !b.moldDone;
    }
  };
  update();
  return { update, cleanup: () => mg?.destroy() };
}

function stageBrine(ui: UI, el: HTMLElement, b: B.Batch): RenderResult {
  const game = ui.game;
  el.innerHTML = `
    <div class="muted">Submerge the squares in brine for <b>${TALEGGIO.BRINE_MIN_HOURS} to ${TALEGGIO.BRINE_MAX_HOURS} hours</b>. Under-brined rind never develops; over-brined cheese is salty. The tub keeps working overnight, so mind the clock.</div>
    <div class="row"><button class="btn primary sub">Submerge (1 salt)</button><span class="muted salt"></span></div>
    <div class="brining" style="display:none">
      <div class="row"><span class="temp btime"></span><span class="muted">hours in brine</span></div>
      <div class="progress"><div class="bbar"></div></div>
      <div class="row"><button class="btn primary pull">Pull from the brine</button><span class="muted bwin"></span></div>
    </div>`;
  const sub = $<HTMLButtonElement>(el, '.sub');
  const salt = $(el, '.salt');
  const brining = $(el, '.brining');
  const btime = $(el, '.btime');
  const bbar = $(el, '.bbar');
  const pull = $<HTMLButtonElement>(el, '.pull');
  const bwin = $(el, '.bwin');
  sub.onclick = () => game.submerge();
  pull.onclick = () => game.pullFromBrine();
  const update = (): void => {
    sub.disabled = b.brineStartMinute !== null;
    salt.textContent = `${game.state.supplies.salt} salt in stock`;
    brining.style.display = b.brineStartMinute !== null ? '' : 'none';
    if (b.brineStartMinute !== null) {
      const h = B.brineHours(b, game.now);
      btime.textContent = fmt(h, 1);
      btime.className = `temp ${h >= TALEGGIO.BRINE_MIN_HOURS && h <= TALEGGIO.BRINE_MAX_HOURS ? 'inband' : h > TALEGGIO.BRINE_MAX_HOURS ? 'hot' : ''}`;
      bbar.style.width = `${Math.min(100, (h / (TALEGGIO.BRINE_MAX_HOURS + 1)) * 100)}%`;
      bwin.textContent = h < TALEGGIO.BRINE_MIN_HOURS ? `${fmt(TALEGGIO.BRINE_MIN_HOURS - h, 1)} h to go` : h <= TALEGGIO.BRINE_MAX_HOURS ? 'In the window. Pull them!' : 'Over-brining! Pull now.';
      bwin.className = h < TALEGGIO.BRINE_MIN_HOURS ? 'muted' : h <= TALEGGIO.BRINE_MAX_HOURS ? 'good' : 'bad';
    }
  };
  update();
  return { update };
}

function stageRack(ui: UI, el: HTMLElement, b: B.Batch): RenderResult {
  const game = ui.game;
  el.innerHTML = `
    <div class="stat"><span>Squares ready</span><b class="units"></b></div>
    <div class="row"><button class="btn primary rack">Age on the cellar racks</button><button class="btn package">Package unaged</button><span class="muted rinfo"></span></div>
    <div class="muted">Aging is optional for player-made cheese. Aged cheese gains value; stars do not change.</div>`;
  const units = $(el, '.units');
  const rack = $<HTMLButtonElement>(el, '.rack');
  const pack = $<HTMLButtonElement>(el, '.package');
  const rinfo = $(el, '.rinfo');
  const preview = document.createElement('p');
  preview.className = 'muted price-preview';
  el.appendChild(preview);
  rack.onclick = () => {
    if (game.rackSquares()) ui.closePanel();
  };
  pack.onclick = () => {
    if (game.packageTaleggioUnaged()) ui.closePanel();
  };
  const update = (): void => {
    units.textContent = `${b.units} × ${starText(B.stars(b))}`;
    preview.textContent = batchPricePreview(ui, b);
    rack.disabled = !game.hasSkill('cellar');
    pack.disabled = b.units <= 0;
    rinfo.textContent = game.hasSkill('cellar') ? '' : 'You can package now or unlock the Cellar to age it.';
  };
  update();
  return { update };
}

// -------------------------------------------------------------------- Sell

export function openSellPanel(ui: UI): void {
  const game = ui.game;
  let channel: Channel = game.hasShop ? 'retail' : 'wholesale';
  ui.panels.open({
    id: 'board',
    title: 'Market board',
    kind: 'menu',
    side: ui.side(),
    render(body): RenderResult {
      body.innerHTML = `
        <div class="row channel"></div>
        <div class="muted mkt"></div>
        <div class="list"></div>`;
      const chan = $(body, '.channel');
      const mkt = $(body, '.mkt');
      const list = $(body, '.list');
      const buildChannel = (): void => {
        chan.innerHTML = '';
        const mk = (c: Channel, label: string, enabled: boolean): void => {
          const btn = document.createElement('button');
          btn.className = `btn small ${channel === c ? 'active' : ''}`;
          btn.textContent = label;
          btn.disabled = !enabled;
          btn.onclick = () => {
            channel = c;
            buildChannel();
            sig = '';
            update();
          };
          chan.appendChild(btn);
        };
        mk('wholesale', 'Wholesale ×1.00', true);
        mk('retail', `Shop ×${fmt(1.35)}`, game.hasShop);
        if (!game.hasShop) chan.insertAdjacentHTML('beforeend', '<span class="muted">Unlock the Shop for retail prices.</span>');
      };
      buildChannel();
      const snap = game.snapshot;
      const bands = Object.values(CHEESES)
        .filter((d) => game.state.codex.learned.includes(d.id))
        .map((d) => {
          const band = retailBand(snap, d.marketKey);
          return band ? `${d.name} ${fmt(band.min)} to ${fmt(band.max)}/lb` : '';
        })
        .filter(Boolean)
        .join(' · ');
      mkt.innerHTML = `Reference prices: <a href="${PRICING_SOURCE_URL}" target="_blank" rel="noopener noreferrer">Cheese Prices Tracker</a> (${PRICING_REVISION}). 1★ uses the low price per pound, 5★ the high price; intermediate stars are evenly spaced: ${bands}. Aging, freshness, demand, channel and upgrades modify the final sale. Costs remain provisional.`;

      let sig = '';
      const priceCells = new Map<number, HTMLElement>();
      const update = (): void => {
        const s = game.state;
        const items = s.inventory.map((item, index) => ({ item, index })).filter((e) => e.item.kind !== 'milk');
        const nextSig = items.map((e) => `${e.index}:${e.item.kind}:${e.item.qty}:${'stars' in e.item ? e.item.stars : ''}`).join('|') + `|${s.day}`;
        if (nextSig !== sig) {
          sig = nextSig;
          priceCells.clear();
          list.innerHTML = items.length ? '' : '<div class="muted">Nothing to sell yet. Pack some cheese first.</div>';
          for (const e of items) {
            const row = document.createElement('div');
            row.className = 'item';
            if (e.item.kind === 'cheese') {
              const def = CHEESES[e.item.cheeseId];
              const age = game.ageDays(e.item);
              const freshNote = def.aging.ageable
                ? `aged ×${fmt(e.item.agedMult ?? 1)} · sell within ${def.aging.shelfDays - age} day${def.aging.shelfDays - age === 1 ? '' : 's'}`
                : `freshness ×${fmt(freshnessMultiplier(def, age))}${age >= def.aging.spoilAfterDays - 1 ? ' · spoils tonight!' : ''}`;
              row.innerHTML = `<div class="product-art">${cheeseArt(def.id)}</div><div class="title">${e.item.qty} × ${def.name} ${starText(e.item.stars)}</div>
                <div class="muted">${def.unitPounds} lb ${def.unitLabel}s · made day ${e.item.madeDay} · ${freshNote}</div>
                <div class="price"></div><div class="row"></div>`;
              const owner = document.createElement('div');
              owner.className = 'muted';
              owner.textContent = `Made by ${e.item.ownerName ?? 'Player'}`;
              row.prepend(owner);
              const r = $(row, '.row');
              const one = document.createElement('button');
              one.className = 'btn small primary';
              one.textContent = 'Sell 1';
              one.onclick = () => {
                if (e.item.kind === 'cheese' && confirm(`Sell 1 × ${def.name} for ${fmt(game.quoteSale(e.item, 1, channel))} coins?`)) game.sell(e.index, 1, channel);
              };
              const all = document.createElement('button');
              all.className = 'btn small';
              all.textContent = 'Sell all';
              all.onclick = () => {
                if (e.item.kind === 'cheese' && confirm(`Sell ${e.item.qty} × ${def.name} for ${fmt(game.quoteSale(e.item, e.item.qty, channel))} coins total?`)) game.sell(e.index, e.item.qty, channel);
              };
              r.append(one, all);
            } else if (e.item.kind === 'whey') {
              row.innerHTML = `<div class="title">${e.item.qty} gal whey</div><div class="muted">Or keep it: ricotta needs ${RICOTTA.MIN_GALLONS} gallons.</div><div class="price"></div><div class="row"></div>`;
              const all = document.createElement('button');
              all.className = 'btn small';
              all.textContent = 'Sell all';
              all.onclick = () => game.sell(e.index, e.item.qty, channel);
              $(row, '.row').appendChild(all);
            }
            priceCells.set(e.index, $(row, '.price'));
            list.appendChild(row);
          }
        }
        for (const e of items) {
          const cell = priceCells.get(e.index);
          if (!cell) continue;
          if (e.item.kind === 'cheese') {
            const p = game.cheesePrice(e.item, channel);
            const def = CHEESES[e.item.cheeseId];
            const sold = s.market.soldThisWeek[def.marketKey] ?? 0;
            const agePart = def.aging.ageable ? ` × aged ${fmt(p.aging)}` : ` × fresh ${fmt(p.freshness)}`;
            cell.innerHTML = `<b class="accent">${fmt(game.quoteSale(e.item, 1, channel))} coins next unit · ${fmt(game.quoteSale(e.item, e.item.qty, channel))} coins all</b><br><span class="muted">Base ${fmt(p.base, 4)} coins/lb × ${p.pounds} lb${agePart} × demand ${fmt(p.dilution)} × channel ${fmt(p.channel)}${p.negotiation !== 1 ? ` × nego ${fmt(p.negotiation)}` : ''} · sold ${sold}/${marketDepth(game.snapshot, def.marketKey)} this week. Demand is applied to each unit in a bulk sale.</span>`;
          } else if (e.item.kind === 'whey') {
            cell.innerHTML = `<b class="accent">${fmt(game.wheyPrice())} coins</b> per gallon`;
          }
        }
      };
      update();
      return { update };
    },
  });
}

export function openTechPanel(ui: UI): void { ui.openResearch(); }

// -------------------------------------------------------------------- Desk

export function openDeskPanel(ui: UI): void {
  const game = ui.game;
  ui.panels.open({
    id: 'desk',
    title: 'Supply desk',
    kind: 'menu',
    side: ui.side(),
    render(body): RenderResult {
      body.innerHTML = `<div class="stat"><span>Coins</span><b class="gold accent"></b></div><div class="list"></div>
        <div class="section"><div class="stat"><span>Vat durability</span><b class="dur"></b></div>
        <div class="row"><button class="btn repair">Repair vat (${VAT.REPAIR_COST} coins)</button></div></div>`;
      const coins = $(body, '.gold');
      const list = $(body, '.list');
      const dur = $(body, '.dur');
      const repair = $<HTMLButtonElement>(body, '.repair');
      repair.onclick = () => game.repairVat();
      const haveCells = new Map<SupplyId, HTMLElement>();
      const buyBtns = new Map<SupplyId, HTMLButtonElement[]>();
      (Object.keys(SUPPLIES) as SupplyId[]).forEach((id) => {
        const def = SUPPLIES[id];
        const row = document.createElement('div');
        row.className = 'item';
        row.innerHTML = `<div class="title">${def.label} <span class="muted">${fmt(def.price)} / ${def.unit}</span></div><div class="muted">Have: <b class="have"></b> <span class="req"></span></div><div class="row"></div>`;
        const r = $(row, '.row');
        const btns: HTMLButtonElement[] = [];
        for (const n of [1, 5, 10]) {
          const btn = document.createElement('button');
          btn.className = 'btn small';
          btn.textContent = `Buy ${n} (${fmt(def.price * n)})`;
          btn.onclick = () => game.buySupply(id, n);
          btns.push(btn);
          r.appendChild(btn);
        }
        buyBtns.set(id, btns);
        haveCells.set(id, $(row, '.have'));
        const blocked = game.canBuySupply(id);
        if (blocked) $(row, '.req').textContent = `· ${blocked}`;
        list.appendChild(row);
      });
      const update = (): void => {
        const s = game.state;
        coins.textContent = fmt(s.gold);
        dur.textContent = `${s.vat.durability}/${VAT.DURABILITY_MAX}`;
        dur.className = game.vatWorn ? 'bad' : '';
        repair.disabled = s.vat.durability >= VAT.DURABILITY_MAX || s.gold < VAT.REPAIR_COST;
        (Object.keys(SUPPLIES) as SupplyId[]).forEach((id) => {
          const have = haveCells.get(id);
          if (have) have.textContent = String(s.supplies[id]);
          const blocked = game.canBuySupply(id);
          buyBtns.get(id)?.forEach((btn, i) => (btn.disabled = !!blocked || s.gold < SUPPLIES[id].price * [1, 5, 10][i]));
        });
      };
      update();
      return { update };
    },
  });
}

// --------------------------------------------------------------- Inventory

export function openInventoryPanel(ui: UI): void {
  const game = ui.game;
  ui.panels.open({
    id: 'inventory',
    title: 'Inventory',
    kind: 'menu',
    side: ui.side(),
    render(body, api): RenderResult {
      body.innerHTML = `
        <div class="stat"><span>Energy</span><b class="energy"></b></div>
        <div class="list"></div>
        <div class="section"><h4>Supplies</h4><div class="supplies muted"></div></div>
        <div class="section"><h4>XP</h4><div class="xp muted"></div><div class="muted">Bars for every process: Mastery (M).</div></div>
        <div class="section"><h4>Settings & save</h4>
          <label class="row"><input type="checkbox" class="relaxed"> Relaxed timing (windows ×1.5)</label>
          <label class="row"><input type="checkbox" class="devmode"> Dev mode: unlock every skill, recipe, and process; pin energy (for testing)</label>
          <div class="row"><button class="btn small export">Export save</button><button class="btn small import">Import save</button><button class="btn small danger reset">New game</button></div>
          <textarea class="save" placeholder="Save string appears here / paste one to import"></textarea>
        </div>`;
      const energy = $(body, '.energy');
      const list = $(body, '.list');
      const supplies = $(body, '.supplies');
      const xp = $(body, '.xp');
      const relaxed = $<HTMLInputElement>(body, '.relaxed');
      const ta = $<HTMLTextAreaElement>(body, '.save');
      relaxed.checked = game.state.settings.relaxedTiming;
      relaxed.onchange = () => {
        game.state.settings.relaxedTiming = relaxed.checked;
        game.emit('change', undefined);
      };
      const devmode = $<HTMLInputElement>(body, '.devmode');
      devmode.checked = game.devMode;
      devmode.onchange = () => {
        if (devmode.checked) game.enableDevMode();
        else game.disableDevMode();
      };
      $<HTMLButtonElement>(body, '.export').onclick = () => {
        ta.value = exportSave(game.state);
        ta.select();
        game.toast('Save string ready. Copy it somewhere safe.', 'info');
      };
      $<HTMLButtonElement>(body, '.import').onclick = () => {
        const next = importSave(ta.value);
        if (!next) {
          game.toast('That save string could not be read.', 'bad');
          return;
        }
        game.replaceState(next);
        api.close();
        game.toast('Save imported.', 'good');
      };
      $<HTMLButtonElement>(body, '.reset').onclick = () => {
        if (confirm('Start a new game? The current save is erased.')) {
          game.reset();
          api.close();
        }
      };
      let sig = '';
      const update = (): void => {
        const s = game.state;
        energy.textContent = `${Math.round(s.energy)} / 100${game.lowEnergy ? ' (low)' : ''}`;
        energy.className = game.lowEnergy ? 'bad' : '';
        supplies.textContent = (Object.keys(SUPPLIES) as SupplyId[]).map((id) => `${SUPPLIES[id].label} ${s.supplies[id]}`).join(' · ');
        xp.innerHTML = `Overall level ${game.overallLevel} (${s.xp.total} xp)<br>` + (Object.keys(TASK) as (keyof typeof TASK)[]).map((t) => `${TASK[t].label} L${game.taskLevel(t)}`).join(' · ');
        const nextSig = s.inventory.map((i, idx) => `${idx}:${i.kind}:${i.qty}:${'stars' in i ? i.stars : ''}:${'quality' in i ? i.quality : ''}`).join('|') + `|${s.day}`;
        if (nextSig === sig) return;
        sig = nextSig;
        list.innerHTML = s.inventory.length ? '' : '<div class="muted">Empty. Go milk Bessie.</div>';
        s.inventory.forEach((item, index) => {
          const row = document.createElement('div');
          row.className = 'item';
          if (item.kind === 'milk') {
            row.innerHTML = `<div class="title">${item.qty} gal raw milk</div><div class="muted">Quality ${fmt(item.quality, 1)} · ${item.cream}% cream · pour it into the vat</div>`;
          } else if (item.kind === 'cheese') {
            const def = CHEESES[item.cheeseId];
            const restore = def.energyRestore.base + def.energyRestore.perStar * item.stars;
            row.innerHTML = `<div class="product-art">${cheeseArt(def.id)}</div><div class="title">${item.qty} × ${def.name} ${starText(item.stars)}</div><div class="muted">Made day ${item.madeDay}${item.agedMult ? ` · aged ×${fmt(item.agedMult)}` : ''} · eat for +${restore} energy</div><div class="row"></div>`;
            const btn = document.createElement('button');
            btn.className = 'btn small';
            btn.textContent = 'Eat one';
            btn.onclick = () => game.eat(index);
            $(row, '.row').appendChild(btn);
          } else {
            row.innerHTML = `<div class="title">${item.qty} gal whey</div><div class="muted">Quality ${fmt(item.quality, 1)} · ricotta needs ${RICOTTA.MIN_GALLONS} gal · drink for +4 energy · or sell it</div><div class="row"></div>`;
            const btn = document.createElement('button');
            btn.className = 'btn small';
            btn.textContent = 'Drink a gallon';
            btn.onclick = () => game.eat(index);
            $(row, '.row').appendChild(btn);
          }
          list.appendChild(row);
        });
      };
      update();
      return { update };
    },
  });
}

// ------------------------------------------------------------------ Skills

export function openSkillsPanel(ui: UI): void { ui.openSkills(); }

// ----------------------------------------------------------------- End day

export function openEndDayPanel(ui: UI): void {
  const game = ui.game;
  ui.panels.open({
    id: 'endday',
    title: 'End the day?',
    kind: 'menu',
    side: ui.side(),
    render(body, api): RenderResult {
      const s = game.state;
      const overtime = Math.floor(Math.max(0, s.minuteOfDay - game.workDayEnd) / 60);
      const cheese = s.inventory.filter((i): i is InventoryCheese => i.kind === 'cheese');
      const unsold = cheese.reduce((n, c) => n + c.qty, 0);
      const staff = s.staff.employees.filter((e) => e.presentToday);
      const wages = staff.reduce((n, e) => n + e.wage, 0);
      body.innerHTML = `
        <div class="stat"><span>Overtime worked</span><b>${overtime} h${overtime ? ` (−${overtime * 5} energy tomorrow)` : ''}</b></div>
        <div class="stat"><span>Unsold cheese</span><b>${unsold} units${unsold ? ' (fresh cheese loses value overnight)' : ''}</b></div>
        <div class="stat"><span>Vat</span><b>${s.vat.batch ? '<span class="bad">A batch is in the vat and will spoil!</span>' : 'Empty'}</b></div>
        ${s.table.batch || s.tub.batch ? `<div class="stat"><span>Table / tub</span><b>Keep working overnight (mind the brine clock)</b></div>` : ''}
        ${s.staff.employees.length ? `<div class="stat"><span>Staff</span><b>${staff.length} working today, wages ${fmt(wages)} coins</b></div>` : ''}
        ${s.cellar.racks.length ? `<div class="stat"><span>Cellar</span><b>${s.cellar.racks.reduce((n, r) => n + r.qty, 0)} wheels aging</b></div>` : ''}
        <div class="row"><button class="btn primary sleep">Sleep until 06:00</button><button class="btn cancel">Keep working</button></div>`;
      $<HTMLButtonElement>(body, '.sleep').onclick = () => {
        api.close();
        game.endDay();
      };
      $<HTMLButtonElement>(body, '.cancel').onclick = () => api.close();
      return {};
    },
  });
}
